import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { Search, ThumbsUp, ThumbsDown, Eye, TrendingUp, ChevronRight } from 'lucide-react';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Breadcrumb } from '../components/ui/Breadcrumb';

interface FAQCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  display_order: number;
}

interface FAQArticle {
  id: string;
  category_id: string;
  question: string;
  answer: string;
  keywords: string[];
  view_count: number;
  helpful_count: number;
  not_helpful_count: number;
}

export function FAQViewerPage() {
  const { currentTenant } = useAuth();
  const [categories, setCategories] = useState<FAQCategory[]>([]);
  const [articles, setArticles] = useState<FAQArticle[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<FAQArticle | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [feedbackGiven, setFeedbackGiven] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (currentTenant) {
      loadCategories();
      loadArticles();
    }
  }, [currentTenant]);

  const loadCategories = async () => {
    const { data, error } = await supabase
      .from('faq_categories')
      .select('*')
      .eq('tenant_id', currentTenant)
      .order('display_order', { ascending: true });

    if (!error && data) {
      setCategories(data);
    }
    setLoading(false);
  };

  const loadArticles = async () => {
    const { data, error } = await supabase
      .from('faq_articles')
      .select('*')
      .eq('tenant_id', currentTenant)
      .eq('is_published', true)
      .order('helpful_count', { ascending: false });

    if (!error && data) {
      setArticles(data);
    }
  };

  const incrementViewCount = async (articleId: string) => {
    await supabase.rpc('increment_faq_view', { article_id: articleId });
    await loadArticles();
  };

  const handleFeedback = async (articleId: string, isHelpful: boolean) => {
    if (feedbackGiven[articleId]) return;

    const field = isHelpful ? 'helpful_count' : 'not_helpful_count';
    const { data: article } = await supabase
      .from('faq_articles')
      .select(field)
      .eq('id', articleId)
      .single();

    if (article) {
      await supabase
        .from('faq_articles')
        .update({ [field]: article[field] + 1 })
        .eq('id', articleId);

      setFeedbackGiven({ ...feedbackGiven, [articleId]: true });
      await loadArticles();
    }
  };

  const handleArticleClick = (article: FAQArticle) => {
    setSelectedArticle(article);
    incrementViewCount(article.id);
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch = searchTerm === '' ||
      article.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.keywords.some(k => k.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = !selectedCategory || article.category_id === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const popularArticles = [...articles]
    .sort((a, b) => b.view_count - a.view_count)
    .slice(0, 5);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (selectedArticle) {
    const category = categories.find(c => c.id === selectedArticle.category_id);

    return (
      <DashboardLayout>
        <div className="p-8 max-w-4xl mx-auto space-y-6">
          <div className="flex items-center gap-2 text-sm text-white/60">
            <button onClick={() => setSelectedArticle(null)} className="hover:text-white transition-colors">
              FAQ
            </button>
            <ChevronRight className="w-4 h-4" />
            <button
              onClick={() => {
                setSelectedCategory(selectedArticle.category_id);
                setSelectedArticle(null);
              }}
              className="hover:text-white transition-colors"
            >
              {category?.name || 'Uncategorized'}
            </button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{selectedArticle.question}</span>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 border border-white/20">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{selectedArticle.question}</h1>

            <div className="flex items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {selectedArticle.view_count} views
              </div>
              <div className="flex items-center gap-1">
                <ThumbsUp className="w-4 h-4" />
                {selectedArticle.helpful_count} helpful
              </div>
            </div>

            <div className="prose prose-lg max-w-none mb-8">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {selectedArticle.answer}
              </p>
            </div>

            {selectedArticle.keywords.length > 0 && (
              <div className="flex gap-2 mb-6 flex-wrap">
                {selectedArticle.keywords.map((keyword, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            )}

            <div className="border-t border-gray-200 pt-6">
              <p className="text-gray-700 font-medium mb-3">Was this article helpful?</p>
              <div className="flex gap-3">
                <Button
                  variant={feedbackGiven[selectedArticle.id] ? 'secondary' : 'primary'}
                  onClick={() => handleFeedback(selectedArticle.id, true)}
                  disabled={feedbackGiven[selectedArticle.id]}
                  className="flex items-center gap-2"
                >
                  <ThumbsUp className="w-4 h-4" />
                  Yes, helpful
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleFeedback(selectedArticle.id, false)}
                  disabled={feedbackGiven[selectedArticle.id]}
                  className="flex items-center gap-2"
                >
                  <ThumbsDown className="w-4 h-4" />
                  Not helpful
                </Button>
              </div>
              {feedbackGiven[selectedArticle.id] && (
                <p className="text-sm text-green-600 mt-2">Thank you for your feedback!</p>
              )}
            </div>
          </div>

          <Button variant="secondary" onClick={() => setSelectedArticle(null)}>
            Back to FAQ
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8 space-y-6">
        <Breadcrumb className="mb-4" />
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Help Center</h1>
          <p className="text-white/60">Find answers to common questions and learn how to use the platform</p>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/20">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search for answers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <h3 className="font-semibold text-gray-900 mb-3">Categories</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    selectedCategory === null
                      ? 'bg-blue-100 text-blue-700'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  All Articles ({articles.length})
                </button>
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    {category.name} ({articles.filter(a => a.category_id === category.id).length})
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Popular Articles
              </h3>
              <div className="space-y-2">
                {popularArticles.map(article => (
                  <button
                    key={article.id}
                    onClick={() => handleArticleClick(article)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-gray-700 text-sm transition-colors"
                  >
                    {article.question}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-4">
            {filteredArticles.length === 0 ? (
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-12 border border-white/20 text-center">
                <p className="text-gray-500">
                  {searchTerm
                    ? 'No articles match your search. Try different keywords.'
                    : 'No FAQ articles available yet.'}
                </p>
              </div>
            ) : (
              filteredArticles.map(article => {
                const category = categories.find(c => c.id === article.category_id);

                return (
                  <button
                    key={article.id}
                    onClick={() => handleArticleClick(article)}
                    className="w-full bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:border-primary-400/50 transition-all text-left group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {category && (
                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                              {category.name}
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors mb-2">
                          {article.question}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                          {article.answer}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {article.view_count}
                          </span>
                          <span className="flex items-center gap-1">
                            <ThumbsUp className="w-3 h-3" />
                            {article.helpful_count}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600 transition-colors flex-shrink-0 mt-1" />
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
